// React
import React from 'react';
import cloneDeep from 'lodash/cloneDeep';

// Form
import { FormProvider, useFormContext, useWatch } from 'react-hook-form';

// Redux
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as actions from '../../../store/actions/actions';

// Material
import { Button } from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import Alert from '@material-ui/lab/Alert';

// Class
import Basics from '../basics/basics';
import Preferences from '../preferences/preferences';
import Accounts from '../accounts/accounts';
import Data from '../data/data';
import TrustedDevices from '../deviceTrust/trustedDevices';
import { FormattedMessage } from "react-intl";
import { injectIntl } from 'react-intl';
import {flags, configs, external_links} from "../../../feature_flags";
import ProfileService, { UserProfileAPI } from '../../../services/profileService';
import IdentityService, { IdentityAPI } from '../../../services/identityService';
import { areNameFieldsLocked } from '../../../utils';

// Utils
const FORM_FIELDS = ['firstName', 'lastName', 'country', 'state', 'language', 'timezone'];
const NAME_FIELDS = ['firstName', 'lastName'];

const getEditableFormFields = (namesLocked) =>
     namesLocked
          ? FORM_FIELDS.filter((field) => !NAME_FIELDS.includes(field))
          : FORM_FIELDS;

const normalizeForCompare = (val) => {
     if (val == null || val === '') return '';
     return String(val).trim();
};

const verifyUpdates = (user, formHooks, namesLocked) => {
     if (!formHooks || Object.keys(formHooks.errors || {}).length !== 0) return false;
     if (!user) return false;
     window.user = cloneDeep(user);
     const formValues = formHooks.getValues();
     let isChangeExists = false;
     getEditableFormFields(namesLocked).forEach((field) => {
          if (normalizeForCompare(user[field]) !== normalizeForCompare(formValues[field])) {
               isChangeExists = true;
               window.user[field] = formValues[field];
          }
     });
     return isChangeExists;
};

/**
 * ActionButtons - re-renders when form values change (via useWatch) so Save/Cancel
 * correctly enable/disable when user edits or manually reverts changes.
 */
const ActionButtons = ({ user, onSave, onCancel, namesLocked }) => {
     const formHooks = useFormContext();
     useWatch({ control: formHooks.control });
     const isUpdated = verifyUpdates(user, formHooks, namesLocked);
     return (
          <div className="actions">
               <Button
                    type="button"
                    variant="contained"
                    size="large"
                    disableElevation
                    className="buttonLink"
                    onClick={onCancel}
                    disabled={!isUpdated}
               >
                    <FormattedMessage defaultMessage="Cancel" />
               </Button>
               <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    size="large"
                    disableElevation
                    onClick={onSave}
                    disabled={!isUpdated}
               >
                    <FormattedMessage defaultMessage="Save" />
               </Button>
          </div>
     );
};



class UserForm extends React.Component {

     /**
	 * @function constructor
	 * @param {*} props - Having properties passed by parent element
	 *  * used for state initialization
	 */
     constructor(props) {
          super(props);
          this.state = {
               user: null,
               changeStatus: 0,
               modified: false,
               error: {},
               message: "",
               onChanges: this.onChanges.bind(this)
          };
     }

     /**
     * @function componentDidMount
     * @description called when the component in first time mounted.
     *  this will be called before parent mounting and after child mounting
     */
     componentDidMount() {
          if (!this.props.user?.firstName) {
               this.loadMyDetails();
               this.loadIdentityDetails();
          }


     }

     onChanges() {
          this.reload(true);
     }


     /**
     * @function render
     * @description called when the component changes its state, props value
     */
     render() {
          const showTrustedDevices = flags.show_trusted_devices && !this.props.isMfaExists && this.props.isNativeUser;
          return (
               <FormProvider {...window.formHooks}>
               <form className="user-form">
                    { this.props.languageCode && configs.unsupported_languages.includes(this.props.languageCode) &&
                    <div >
                         <Alert className='warning-info' severity="warning">
                             <div className='warning-info-flex'> 
                              <FormattedMessage  defaultMessage="Important: For the best experience across Trimble's suite of products and services, select a supported language." />
                             
                             
                              <Button
                                   type="button"
                                   className="link-as-button"
                                   variant="outlined"
                                   disableElevation
                                   onClick={() => window.open(external_links.language_warning, '_blank')} 
                                   >
                                   <FormattedMessage defaultMessage="Learn more" />
                                   </Button>
                                   </div>
                         </Alert>
                    </div>
               }
                    <ActionButtons
                         user={this.props.user}
                         onSave={this.saveUserDetails}
                         onCancel={() => this.reload(false)}
                         namesLocked={this.props.namesLocked}
                    />
                    <div className="form-sections" key="form-sections">
                         <div className="row-sections">
                              <div className="column-sections">
                                   <Basics />
                                   <Preferences />
                                   {((flags.show_revoke_consent && this.props.consented_apps.length > 0) || this.props.isnotFederatedUser) && <Data onChanges={this.state.onChanges}/>}
                              </div>
                              <div className="column-sections">
                                   <Accounts onChanges={this.state.onChanges} saveIdentityDetails={this.saveIdentityDetails} />
                                   {showTrustedDevices && <TrustedDevices onChanges={this.state.onChanges} />}
                              </div>
                         </div>
                    </div>
                    {/* { !this.props.user?.firstName && (
                         <Loader showBackdrop />
                    )} */}
                    <Snackbar 
                         anchorOrigin={{ horizontal: "center", vertical: "top" }}
                         open={(this.state.message !== "") ? true : false}
                         onClose={this.onMessageAutoHide}

                         message={this.state.message}
                         autoHideDuration={6000}
                         className="snackbar"

                    >
                         <MuiAlert elevation={6} variant="filled" severity={this.state.messageType}>{this.state.message}</MuiAlert>
                    </Snackbar>

               </form>
               </FormProvider>
          )
     }

     onMessageAutoHide = () => {
          let isSaveSuccess = (this.state.messageType === "success");
          if (isSaveSuccess) {
               this.setState({ "message": "", messageType: this.state.messageType });
          }
     }
     /**
      * @function loadMyDetails
      * @description to get User details in ProfileX API
      */
     loadMyDetails = () => {

          ProfileService.request(
               {
                    url: UserProfileAPI.parseRequestURL(UserProfileAPI.get_my_details, {}),
                    method: 'GET'
               }
          ).then(
               (response) => {
                    let responseData = response.data;
                    responseData = { ...responseData, uuid: responseData.id || responseData.uuid};  

                    this.props.dispatch(
                         {
                              type: actions.storeUserDetails,
                              payload: responseData
                         }
                    );
                    // this.loadMyPhoto(response.data.id);                    
               }
          ).catch(
               (error) => {

               }
          );

     }

     loadIdentityDetails = () => {
          IdentityService.request(
               {
                    url: IdentityAPI.parseRequestURL(IdentityAPI.get_my_details, {uuid: this.props.uuid}),
                    method: 'GET',
                    baseURL: this.props.endpoints.identityAPI
               }
          ).then(
               (response) => {
                    this.props.dispatch(
                         {
                              type: actions.storeIdentityDetails,
                              payload: response.data
                         }
                    );
               }
          ).catch(
               (error) => {

               }
          )
     }
     saveIdentityDetails = (post_data) => {
          this.setState({
               message: this.props.intl.formatMessage({
                    defaultMessage: "Updating..."
               }), messageType: "info"
          });
          IdentityService.request(
               {
                    url: IdentityAPI.parseRequestURL(IdentityAPI.get_my_details, {uuid: this.props.uuid}),
                    method: 'POST',
                    baseURL: this.props.endpoints.identityAPI,
                    data: post_data
               }
          ).then(
               (response) => {
                    if(response && response.status === 200){
                         this.setState({
                              message: this.props.intl.formatMessage({
                                   defaultMessage: "Updated successfully"
                              }), messageType: "success"
                         });
                         this.loadIdentityDetails();
                    }else{
                         this.setState({
                              message: this.props.intl.formatMessage({
                                   defaultMessage: "Error occured"
                              }), messageType: "error"
                         });
                    }
               }
          ).catch(
               (error) => {
                    this.setState({
                         message: this.props.intl.formatMessage({
                              defaultMessage: "Error occured"
                         }), messageType: "error"
                    });
               }
          )
     }
     reload = (isReloadCall) => {
          let user = this.props.user;
          this.props.dispatch(
               {
                    type: actions.clearUserDetails
               }
          );
          if (isReloadCall) {
               this.loadMyDetails();
               this.loadIdentityDetails();
               return;
          }
          setTimeout(() => {
               this.props.dispatch(
                    {
                         type: actions.storeUserDetails,
                         payload: user
                    }
               );
               const resetValues = FORM_FIELDS.reduce((agg, field) => {
                    agg[field] = user?.[field] ?? '';
                    return agg;
               }, {});
               window.formHooks.reset(resetValues);
          }, 2);

     }

     saveUserDetails = () => {
          if (!verifyUpdates(this.props.user, window.formHooks, this.props.namesLocked)) {
               return;
          }
          this.setState({
               message: this.props.intl.formatMessage({
                    defaultMessage: "Saving..."
               }), messageType: "info"
          });
          let apiCall = UserProfileAPI.update_user;

          let payload = getEditableFormFields(this.props.namesLocked).reduce((agg, val) => {
               if (window.user[val] !== this.props.user[val]) {
                    agg[val] = (window.user[val] || "").length > 0 ? window.user[val] : undefined;
               }
               return agg;
          }, {});

          if(flags.show_state_dropdown ){
               if((payload.country === "US" || payload.country === "CA") && (!payload.state || !payload.state.startsWith(payload.country))){
                    this.setState({ message: "State selection is required", messageType: "error" });
                    return;
               }
          }
          
          ProfileService.put(
               UserProfileAPI.parseRequestURL(apiCall, { userId: window.user.uuid}),
               payload
          ).then(
               (response) => {
                    this.setState({
                         message: this.props.intl.formatMessage({
                              defaultMessage: "Changes Saved successfully..."
                         }), messageType: "success"
                    });

                    setTimeout(() => {
                         this.reload(true);
                    }, 100)
               }
          ).catch(
               (error) => {
                    this.setState({ message: error.response?.data?.message, messageType: "error" });
               }
          );
     }
}


/**
 * For mapping necessary state variables to current component properties
 * @param {Object} state - Immutable State Object
 * @return properties needed from state 
 */
const mapStateToProps = (state) => {
     const regionEndpoints = {
          'us': process.env.REACT_APP_US_IDENTITY_ENDPOINT,
          'eu': process.env.REACT_APP_EU_IDENTITY_ENDPOINT,
          'uk': process.env.REACT_APP_UK_IDENTITY_ENDPOINT,
          'eu-gb': process.env.REACT_APP_UK_IDENTITY_ENDPOINT,
          'ap-au': process.env.REACT_APP_AU_IDENTITY_ENDPOINT
     };

     const federation_origin = (state.auth?.profile?.federation_origin) ?? null;
     return {
          countries: state.common.countries,        
          accessToken: state.auth.access_token,
          user: state.userManager,
          languageCode: state.userManager?.language,
          uuid: state.auth?.profile?.sub,
          endpoints: {
               identityAPI: regionEndpoints[state.auth?.profile?.data_region ?? "us"]
          },
          isnotFederatedUser: federation_origin || state.userManager?.identity?.has_federations ? false : true,
          namesLocked: areNameFieldsLocked(state.auth?.profile, state.userManager?.identity),
          federation_origin,
          hasIdentity: (state.userManager?.identity) ?? null,
          consented_apps: (state.userManager?.identity?.consented_application) ?? [],
          isMfaExists: (Object.keys(state.userManager?.identity?.mfa_config?.configured_mfa_settings ?? {})?.length > 0),
          isNativeUser: ((state.auth?.profile?.amr ?? [])?.some(value => (value === "password" || value === "passkey")))
     } 
};

export default withRouter(connect(mapStateToProps)(injectIntl(UserForm)));